import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Leaf, Mail, Phone, MapPin, Clock } from "lucide-react";

export default function Footer({ onOpenCookiePrefs }) {
  const { t } = useTranslation();
  const { lang = "fr" } = useParams();
  const base = `/${lang}`;

  return (
    <footer className="vx-footer" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* About */}
          <div>
            <Link to={base} className="flex items-center gap-2 mb-4" data-testid="footer-logo">
              <span className="inline-flex items-center justify-center w-8 h-8" style={{ backgroundColor: "var(--vx-emerald)" }}>
                <Leaf size={18} color="white" />
              </span>
              <span className="font-serif-display text-xl font-bold text-white">{t("brand.name")}</span>
            </Link>
            <h4 className="text-white text-sm font-semibold mb-2">{t("footer.about_title")}</h4>
            <p className="text-sm leading-relaxed text-slate-300">{t("footer.about_body")}</p>
          </div>

          {/* Explore */}
          <div>
            <h4 className="text-white text-sm font-semibold mb-4 uppercase tracking-wider" style={{ fontSize: "0.75rem" }}>{t("footer.explore_title")}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to={base} data-testid="footer-link-home">{t("footer.links.home")}</Link></li>
              <li><Link to={`${base}/privacy-policy`} data-testid="footer-link-privacy">{t("footer.links.privacy")}</Link></li>
              <li><Link to={`${base}/cookie-policy`} data-testid="footer-link-cookies">{t("footer.links.cookies")}</Link></li>
              <li><Link to={`${base}/terms`} data-testid="footer-link-terms">{t("footer.links.terms")}</Link></li>
              <li>
                <button
                  type="button"
                  onClick={onOpenCookiePrefs}
                  data-testid="footer-cookie-prefs"
                  className="underline underline-offset-4"
                >
                  {t("cookie_banner.customize")}
                </button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white text-sm font-semibold mb-4 uppercase tracking-wider" style={{ fontSize: "0.75rem" }}>{t("footer.contact_title")}</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex gap-2"><MapPin size={16} className="mt-0.5 shrink-0" style={{ color: "var(--vx-emerald)" }} /><span>{t("footer.contact_address")}</span></li>
              <li className="flex gap-2"><Mail size={16} className="mt-0.5 shrink-0" style={{ color: "var(--vx-emerald)" }} /><a href={`mailto:${t("footer.contact_email")}`} data-testid="footer-email">{t("footer.contact_email")}</a></li>
              <li className="flex gap-2"><Phone size={16} className="mt-0.5 shrink-0" style={{ color: "var(--vx-emerald)" }} /><a href={`tel:${t("footer.contact_phone").replace(/\s/g,'')}`} data-testid="footer-phone">{t("footer.contact_phone")}</a></li>
              <li className="flex gap-2"><Clock size={16} className="mt-0.5 shrink-0" style={{ color: "var(--vx-emerald)" }} /><span>{t("footer.hours")}</span></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-white text-sm font-semibold mb-4 uppercase tracking-wider" style={{ fontSize: "0.75rem" }}>{t("footer.legal_title")}</h4>
            <ul className="space-y-1.5" data-testid="footer-legal">
              <li className="vx-legal-line">{t("footer.capital")}</li>
              <li className="vx-legal-line">{t("footer.siren")}</li>
              <li className="vx-legal-line">{t("footer.siret")}</li>
              <li className="vx-legal-line">{t("footer.vat")}</li>
              <li className="vx-legal-line">{t("footer.ape")}</li>
              <li className="vx-legal-line">{t("footer.director")}</li>
              <li className="vx-legal-line">{t("footer.incorporation")}</li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="vx-legal-line">{t("footer.authority")}</div>
          <div className="vx-legal-line">{t("footer.jurisdiction")}</div>
        </div>
        <div className="mt-3 vx-legal-line">{t("footer.rights")}</div>
      </div>
    </footer>
  );
}
